import { useEffect, useState } from 'react';

import { focusManager, QueryClient } from '@tanstack/react-query';

import { retryDelayMs, TvUnpairedError } from 'src/tv/tvClient';

/** How often a paired screen re-reads `/tv/dashboard` and `/tv/me`. */
export const TV_REFRESH_MS = 60_000;

/**
 * The wall screen's own QueryClient (`TvApp` runs outside the agent app's providers). Memory only: no
 * persister, since a TV must not keep a cache in localStorage, and no devtools.
 *
 * Every TV query polls once a minute, also while the tab is hidden (nobody focuses a wall screen). A
 * failure keeps the last data and retries forever on `retryDelayMs` (3 s doubling to a minute); a 401
 * (`TvUnpairedError`) is never retried and stops the polling, the screen goes back to pairing.
 *
 * It also makes TanStack treat the page as always focused. TanStack pauses a failing query's retries while
 * the tab is hidden, and a paused query skips its interval refetch too, so one failure in a hidden tab would
 * stop all polling until the tab is shown. A TV can report itself hidden for hours (occlusion tracking, a
 * sleeping display). `focusManager` is global, which is safe here: `/mb-dash` and `/kh-dash` render only `TvApp`.
 */
export const createTvQueryClient = (): QueryClient => {
  focusManager.setEventListener(() => undefined);
  focusManager.setFocused(true);

  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: (query) => (query.state.error instanceof TvUnpairedError ? false : TV_REFRESH_MS),
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // Retry on our own backoff whatever the TV's `navigator.onLine` claims, instead of pausing.
        networkMode: 'always',
        retry: (_failureCount, error) => !(error instanceof TvUnpairedError),
        retryDelay: retryDelayMs,
      },
    },
  });
};

/**
 * When the current run of failures began (a query's `failureCount` is above zero while it retries), or
 * null once a fetch succeeds.
 */
export const useFailingSince = (failureCount: number): number | null => {
  const [since, setSince] = useState<number | null>(null);
  const isFailing = failureCount > 0;

  useEffect(() => {
    setSince((current) => (isFailing ? current ?? Date.now() : null));
  }, [isFailing]);

  return since;
};

/** Hands a 401 back to pairing. */
export const useUnpairedOn = (error: Error | null, onUnpaired: () => void): void => {
  useEffect(() => {
    if (error instanceof TvUnpairedError) onUnpaired();
  }, [error, onUnpaired]);
};
